import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, RefreshCw, Save, Info } from "lucide-react";
import AdminShell, { Panel } from "@/components/admin/AdminShell";
import { api, getErrorMessage } from "@/lib/api";

function ModeCard({ value, selected, title, description, badge, color, onClick, disabled }) {
  const active = selected === value;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onClick(value)}
      className="text-left rounded-xl p-4 transition-all w-full h-full disabled:opacity-40"
      style={{
        border: `2px solid ${active ? color : "#1E2845"}`,
        background: active ? `${color}18` : "#0A0F1F",
      }}
    >
      <div className="flex items-center flex-wrap gap-2 mb-2">
        <span className="text-sm font-bold text-white">{title}</span>
        {badge ? (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${color}33`, color }}
          >
            {badge}
          </span>
        ) : null}
        {active ? (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full text-[#0A0F1F] ml-auto"
            style={{ background: color }}
          >
            Active
          </span>
        ) : null}
      </div>
      <p className="text-xs text-[#8892A8] leading-relaxed">{description}</p>
    </button>
  );
}

function StatusChip({ label, ok, detail }) {
  return (
    <div className="rounded-xl border border-[#1E2845] bg-[#0A0F1F] px-3 py-2.5 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[#8892A8] font-semibold">{label}</div>
      <div className={`text-sm font-bold mt-1 ${ok ? "text-emerald-400" : "text-amber-400"}`}>
        {ok ? "Configured" : "Not set"}
      </div>
      {detail ? <div className="text-[11px] text-[#8892A8] mt-0.5">{detail}</div> : null}
    </div>
  );
}

export default function AdminSignzySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [settings, setSettings] = useState(null);
  const [kycMode, setKycMode] = useState("manual");
  const [kycEnabled, setKycEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/admin/kyc/signzy-settings");
      setSettings(data);
      setKycMode(data.kyc_mode || "manual");
      setKycEnabled(data.kyc_enabled !== false);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    setSaving(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.patch("/api/admin/kyc/signzy-settings", {
        kyc_mode: kycMode,
        kyc_enabled: kycEnabled,
      });
      setSettings(data);
      setOk("Settings saved.");
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell
      title="KYC Settings"
      subtitle="Control Digio automatic KYC vs manual document review."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-[#E5E9F2]">
            <input
              type="checkbox"
              checked={kycEnabled}
              onChange={(e) => setKycEnabled(e.target.checked)}
              disabled={loading}
            />
            KYC submissions enabled
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#8892A8] hover:text-white border border-[#1E2845]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-[#0A0F1F] bg-[#F0B83A] disabled:opacity-40"
            >
              <Save size={15} />
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>

        {err ? (
          <div className="rounded-xl px-4 py-3 text-sm text-red-300 bg-red-500/10 border border-red-500/25">
            {err}
          </div>
        ) : null}
        {ok ? (
          <div className="rounded-xl px-4 py-3 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/25">
            {ok}
          </div>
        ) : null}

        <div className="grid lg:grid-cols-[1fr_280px] gap-4 items-start">
          <Panel className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-500/10 border border-violet-500/20 grid place-items-center shrink-0">
                <ShieldCheck size={16} className="text-violet-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white">KYC verification mode</h3>
                <p className="text-xs text-[#8892A8] mt-0.5">
                  How users complete identity verification on web and mobile.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <ModeCard
                value="manual"
                selected={kycMode}
                title="Manual review"
                description="Users upload PAN, Aadhaar, and selfie for admin review."
                color="#f59e0b"
                onClick={setKycMode}
                disabled={loading}
              />
              <ModeCard
                value="auto"
                selected={kycMode}
                title="Auto — Digio"
                badge="DigiStudio"
                description="Digio DigiStudio workflow (DigiLocker / Aadhaar / PAN). Instant approval when Digio completes."
                color="#22c55e"
                onClick={setKycMode}
                disabled={loading}
              />
              <ModeCard
                value="disabled"
                selected={kycMode}
                title="Disabled"
                description="Block all KYC submissions."
                color="#ef4444"
                onClick={setKycMode}
                disabled={loading}
              />
            </div>

            {kycMode === "auto" ? (
              <div className="flex items-start gap-2 rounded-xl px-4 py-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20">
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>
                  Requires <strong>DIGIO_CLIENT_ID</strong>, <strong>DIGIO_CLIENT_SECRET</strong>, and{" "}
                  <strong>DIGIO_TEMPLATE_NAME</strong> in <code>backend/.env</code>. Digio configured:{" "}
                  <strong>{settings?.digio_configured ? "yes" : "no"}</strong>
                  {settings?.digio_template_name
                    ? ` · template ${settings.digio_template_name}`
                    : ""}
                  {settings?.digio_environment ? ` · ${settings.digio_environment}` : ""}.
                </span>
              </div>
            ) : null}
          </Panel>

          <Panel className="p-4 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#8892A8]">
              Integration status
            </h3>
            <div className="grid grid-cols-1 gap-2">
              <StatusChip
                label="Digio DigiStudio"
                ok={Boolean(settings?.digio_configured)}
                detail={
                  settings?.digio_configured
                    ? `${settings?.digio_environment || "sandbox"} · ${settings?.digio_template_name || "template set"}`
                    : "Set DIGIO_CLIENT_ID / SECRET / TEMPLATE_NAME"
                }
              />
              <StatusChip
                label="Face match (manual)"
                ok={Boolean(settings?.face_match_configured)}
                detail={settings?.face_match_required ? "Required for submit" : "Optional"}
              />
              <StatusChip
                label="PAN verify (legacy)"
                ok={Boolean(settings?.pan_verify_configured)}
                detail={settings?.signzy_configured ? "Signzy still available" : "Optional"}
              />
            </div>
          </Panel>
        </div>
      </div>
    </AdminShell>
  );
}
